import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  AccessToken,
  BackendResponse,
  HTTP_BASE_URL,
  TokenAction,
  UserModel,
} from '@qtek/shared/models';
import { catchError, Observable, of } from 'rxjs';
import { BEResponse, RegisterApiPayload } from '../models/api-core.models';

@Injectable({
  providedIn: 'root',
})
export class ApiCoreService {
  private readonly httpBaseUrl: string = inject(HTTP_BASE_URL);
  private readonly httpClient: HttpClient = inject(HttpClient);

  getMetaData<T>(httpParams: HttpParams) {
    return this.httpClient
      .get<BEResponse<T>>(this.httpBaseUrl + '/api/v2/meta', {
        params: httpParams,
      })
      .pipe(catchError(({ error }) => of(error)));
  }

  login(payload: {
    email: string;
    password: string;
    mfa_code?: string;
    mfa_trust?: boolean;
  }) {
    return this.httpClient
      .post<BEResponse<unknown>>(this.httpBaseUrl + '/api/v2/auth', payload, {
        withCredentials: true,
      })
      .pipe(catchError(res => of(res)));
  }

  logout() {
    return this.httpClient.get(this.httpBaseUrl + '/api/v2/auth/logout', {
      withCredentials: true,
    });
  }

  resetPassword(payload: { email: string }) {
    return this.httpClient
      .post<
        BEResponse<unknown>
      >(this.httpBaseUrl + '/api/v2/auth/reset/init', payload)
      .pipe(catchError(({ error }) => of(error)));
  }

  confirmExternalMFA(payload: {
    token: string;
    mfa_code: string;
    mfa_trust: boolean;
  }) {
    return this.httpClient.post<BEResponse<unknown>>(
      this.httpBaseUrl + '/api/v2/auth/token',
      payload,
      {
        withCredentials: true,
      }
    );
  }

  resetPasswordConfirm(payload: {
    email: string;
    pass1: string;
    pass2: string;
    resetToken: string;
  }) {
    return this.httpClient
      .post<
        BEResponse<unknown>
      >(this.httpBaseUrl + '/api/v2/auth/reset', payload)
      .pipe(catchError(({ error }) => of(error)));
  }

  register<T>(payload: RegisterApiPayload) {
    return this.httpClient.post<BEResponse<T>>(
      this.httpBaseUrl + '/api/v2/auth/register',
      payload
    );
  }

  getWsSessionInitToken() {
    return this.httpClient.get<BEResponse<{ wsTkn: string }>>(
      '/api/v2/auth/web-socket-token',
      {
        withCredentials: true,
      }
    );
  }

  getUserIsLogged(path?: string) {
    return this.httpClient.get<BEResponse<UserModel>>(
      `${path ? path : ''}/api/v2/user`,
      {
        withCredentials: true,
      }
    );
  }

  updateUserTheme(userId: string, theme: string) {
    return this.httpClient.put<BEResponse<unknown>>(
      `/api/v2/model/User/${userId}`,
      { theme },
      { withCredentials: true }
    );
  }

  getUserData() {
    return this.httpClient.get<BEResponse<UserModel>>('/api/v2/user', {
      withCredentials: true,
    });
  }

  getDocumentToken(
    docId: string,
    refId: string
  ): Observable<BackendResponse<AccessToken>> {
    return this.httpClient.get<BackendResponse<AccessToken>>(
      '/api/v1/service/doc/tkn/' + docId,
      {
        withCredentials: true,
        params: { refId, action: TokenAction.DOWNLOAD },
      }
    );
  }
}
